/* eslint-disable react/no-array-index-key */
import React, { useCallback, useMemo } from 'react';
import { Button, Flex, Pane, Text, ToggleButton } from '@leverege/ui-elements';
import { useInstanceCallback, useValueChange } from '@leverege/ui-hooks';
import { SharedContext, DnDUtil, DragDropView, useSharedContext } from '@leverege/ui-plugin';
import { AttributeSelector, Attributes } from '@leverege/ui-attributes';
import I18N from '@leverege/i18n';
import ImagineSortModel from "./AttributeSortModel.js";
import ImagineSortFieldModel from "./AttributeSortFieldModel.js";
import S from './AttributeSortModelEditor.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TYPE = 'sortField';
const {
  t,
  tIcon
} = I18N.ns('molten.sort.AttributeSortModelEditor');
const onAddField = ({
  value,
  eventData,
  onChange
}, e) => {
  const nModel = ImagineSortModel.addField(value, ImagineSortFieldModel.create());
  onChange({
    data: eventData,
    value: nModel
  });
};
export default function ImagineSortModelEditor(props) {
  const {
    value,
    relationship,
    context,
    delegate,
    onChange,
    eventData
  } = props;
  const {
    objectType
  } = context;
  const [onDrop, onRemove, onChildChange] = useInstanceCallback([DnDUtil.dropItem, DnDUtil.removeItem, DnDUtil.updateChildItem], ImagineSortModel.fields(), props);
  const onAdd = useInstanceCallback(onAddField, props);
  const pOnRemove = useCallback((...args) => {
    const fields = ImagineSortModel.getFields(value);
    if (fields.length > 1) {
      onRemove(...args);
    } else {
      onChange({
        data: eventData,
        value: ImagineSortModel.create()
      });
    }
  }, [onRemove, value, eventData, onChange]);
  const content = useMemo(() => {
    if (value?.fields?.length > 0) {
      return value.fields.map((field, index) => {
        return /*#__PURE__*/_jsx(SortRow, {
          value: field,
          eventData: field,
          index: index,
          model: ImagineSortModel.fields(),
          onDrop: onDrop,
          onChange: onChildChange,
          onRemove: pOnRemove
        }, field.attrName + index);
      });
    }
    return null;
  }, [value?.fields, onDrop, onChildChange, pOnRemove]);
  return /*#__PURE__*/_jsx(SharedContext, {
    objectType: objectType,
    delegate: delegate,
    relationship: relationship,
    children: /*#__PURE__*/_jsxs(Pane, {
      variant: "sortEditor|editor",
      className: S.fieldsHolder,
      children: [content, /*#__PURE__*/_jsx(Flex, {
        children: /*#__PURE__*/_jsx(Button, {
          disabled: value?.fields?.[value.fields.length - 1]?.attrName == null,
          onClick: onAdd,
          icon: tIcon('addIcon', 'fa fa-plus fa-fw'),
          variant: "sortAdd|link",
          children: t('addLabel')
        })
      })]
    })
  });
}
function SortRow(props) {
  const {
    value,
    index,
    onRemove,
    onDrop
  } = props;
  const onModelChange = useValueChange(ImagineSortFieldModel, props);
  const {
    objectType
  } = useSharedContext();
  const attribute = useMemo(() => {
    if (!value.attrName) {
      return null;
    }
    return Attributes.getAttribute(value.attrName, objectType);
  }, [value.attrName, objectType]);
  const availAttributes = useMemo(() => {
    return Attributes.getAttributesFor(objectType).filter(attr => {
      return attr?.getOption?.('sortable') === true;
    });
  }, [objectType]);
  const isTimestamp = attribute?.valueType === 'timestamp';
  const isBoolean = attribute?.valueType === 'boolean';
  const isNumeric = attribute?.valueType !== 'string' && attribute?.valueType !== 'resourceEnum' && attribute?.valueType !== 'enum' && attribute?.valueType !== 'parentRelationship' && attribute?.valueType !== 'timestamp' && attribute?.valueType !== 'boolean';
  return /*#__PURE__*/_jsx(DragDropView, {
    value: value,
    index: index,
    onRemove: onRemove,
    onDrop: onDrop,
    accept: TYPE,
    dragType: TYPE,
    children: /*#__PURE__*/_jsxs(Pane, {
      layout: "flex:rowMCenter",
      className: S.field,
      children: [/*#__PURE__*/_jsx(Text, {
        className: S.label,
        children: index === 0 ? t('sortBy') : t('thenBy')
      }), /*#__PURE__*/_jsx(AttributeSelector, {
        className: S.attributeSelector,
        values: availAttributes,
        value: ImagineSortFieldModel.getAttrName(value),
        onChange: onModelChange,
        eventData: "setAttrName"
      }), value.attrName != null ? /*#__PURE__*/_jsx(SortToggle, {
        isNumeric: isNumeric,
        isBoolean: isBoolean,
        isTimestamp: isTimestamp,
        value: ImagineSortFieldModel.getOrder(value),
        onChange: onModelChange,
        eventData: "setOrder"
      }) : null, /*#__PURE__*/_jsx(Button, {
        variant: "actionBar",
        eventData: value,
        onClick: onRemove,
        icon: tIcon('removeIcon')
      })]
    })
  });
}
function SortToggle(props) {
  const {
    value,
    onChange,
    eventData,
    isNumeric,
    isTimestamp,
    isBoolean
  } = props;
  const pOnChange = useCallback(({
    value,
    data
  }) => {
    if (value) {
      onChange({
        value: data,
        data: eventData
      });
    }
  }, [eventData, onChange]);
  const arrowRight = tIcon('arrowRightIcon', 'fa fa-fw fa-arrow-right');
  const sortType = isTimestamp ? 'timestamp' : isNumeric ? 'numeric' : isBoolean ? 'boolean' : undefined;
  const leftSortAscValue = t('leftSortAscValue', {
    context: sortType
  });
  const rightSortAscValue = t('rightSortAscValue', {
    context: sortType
  });
  const leftSortDescValue = t('leftSortDescValue', {
    context: sortType
  });
  const rightSortDescValue = t('rightSortDescValue', {
    context: sortType
  });
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "sortToggleBackground|toggleBackground",
    layout: "flex:rowS",
    children: [/*#__PURE__*/_jsxs(ToggleButton, {
      onChange: pOnChange,
      eventData: "asc",
      value: value === 'asc',
      variant: "sort",
      children: [leftSortAscValue, /*#__PURE__*/_jsx("i", {
        className: arrowRight
      }), rightSortAscValue]
    }), /*#__PURE__*/_jsxs(ToggleButton, {
      onChange: pOnChange,
      eventData: "desc",
      value: value === 'desc',
      variant: "sort",
      children: [leftSortDescValue, /*#__PURE__*/_jsx("i", {
        className: arrowRight
      }), rightSortDescValue]
    })]
  });
}